// Loyalty ledger presentation (Sep 2026).
//
// The ledger stores two independent signed quantities per entry, and the
// member drawer was reading only one of them:
//
//   `points_delta`   — what moved in the member's BALANCE.
//   `reserved_delta` — what moved in the member's HELD (reserved) pool.
//
// A `redeem_reserved` entry moves one point into the held pool and moves
// the balance by nothing at all, which is exactly right: a hold is not a
// debit, and the member has not spent anything until the payment is
// finalized. Rendering `points_delta` for it printed the true but
// useless "Held for order 0" — the one number on that row guaranteed to
// be zero.
//
// This module interprets each entry type with the field that actually
// carries its meaning. It NEVER modifies stored ledger records; it reads
// the authoritative fields and says what they mean.
//
// The other correction here is the zero-award earning entry. An order can
// move a member's qualifying spend forward without crossing the earning
// threshold, and the drawer reported that as a bare "Earned 0", which
// reads as "this order did nothing". It did something: it carried the
// member closer to their next point, and `qualifying_cents_delta` is the
// authoritative record of how much.

/** `1 point` / `2 points`, from a non-negative count. */
export function pointsPhrase(count) {
  const n = Math.abs(Math.round(Number(count) || 0));
  return `${n} ${n === 1 ? "point" : "points"}`;
}

/** `$1.00` from an integer number of cents. */
export function centsToDollarText(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return null;
  return `$${(Math.abs(n) / 100).toFixed(2)}`;
}

function intOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
}

const SIGNED = (n) => (n > 0 ? `+${n}` : String(n));

/**
 * Interpret one ledger entry for display.
 *
 * Returns `{ label, amountText, tone, detail }` where
 *   `label`      names what happened,
 *   `amountText` is the figure shown against it,
 *   `tone`       is "credit" | "debit" | "hold" | "neutral" for styling,
 *   `detail`     is optional supporting text (qualifying progress).
 *
 * `tone: "hold"` exists so a reservation is never coloured or signed like
 * a balance movement — it is not one.
 */
export function describeLedgerEntry(entry) {
  const e = entry || {};
  const type = String(e.entry_type || "");
  const pointsDelta = intOrNull(e.points_delta) ?? 0;
  const reservedDelta = intOrNull(e.reserved_delta);
  const qualifying = intOrNull(e.qualifying_cents_delta);

  switch (type) {
    case "redeem_reserved": {
      // Reservation semantics: the HELD pool is what moved.
      const held = reservedDelta == null ? null : Math.abs(reservedDelta);
      return {
        label: "Held for order",
        amountText: held == null ? "held" : pointsPhrase(held),
        tone: "hold",
        detail: null,
      };
    }
    case "redeem_released": {
      const released = reservedDelta == null ? null : Math.abs(reservedDelta);
      return {
        label: "Hold released",
        amountText: released == null ? "released" : pointsPhrase(released),
        tone: "hold",
        detail: null,
      };
    }
    case "redeem_finalized": {
      // Finalization is the moment the points are actually spent: the
      // balance moves, and the magnitude comes from `points_delta`.
      const used = Math.abs(pointsDelta || (reservedDelta == null ? 0 : reservedDelta));
      return {
        label: "Used",
        amountText: `− ${pointsPhrase(used)}`,
        tone: "debit",
        detail: null,
      };
    }
    case "earn":
    case "earn_reversal": {
      const spendText = qualifying == null ? null : centsToDollarText(qualifying);
      const earnedPhrase = `${Math.abs(pointsDelta)} ${Math.abs(pointsDelta) === 1 ? "point" : "points"}`;
      if (pointsDelta === 0) {
        // A qualifying purchase that awarded nothing still moved the
        // member toward their next point. Say so.
        const direction = qualifying != null && qualifying < 0 ? "reversed" : "qualifying spend";
        return {
          label: "Purchase progress",
          amountText: "0",
          tone: "neutral",
          detail: spendText
            ? `${spendText} ${direction}; 0 points earned`
            : "0 points earned",
        };
      }
      return {
        label: type === "earn" ? "Earned" : "Earning reversed",
        amountText: SIGNED(pointsDelta),
        tone: pointsDelta > 0 ? "credit" : "debit",
        detail: spendText
          ? `${spendText} qualifying spend; ${earnedPhrase} ${pointsDelta > 0 ? "earned" : "reversed"}`
          : null,
      };
    }
    case "redeem_restored":
      return {
        label: "Returned by refund",
        amountText: SIGNED(pointsDelta),
        tone: pointsDelta >= 0 ? "credit" : "debit",
        detail: null,
      };
    case "manual_credit":
    case "manual_debit":
      return {
        label: type === "manual_credit" ? "Manual credit" : "Manual debit",
        amountText: SIGNED(pointsDelta),
        tone: pointsDelta >= 0 ? "credit" : "debit",
        detail: null,
      };
    default:
      return {
        label: type || "Points adjustment",
        amountText: SIGNED(pointsDelta),
        tone: pointsDelta > 0 ? "credit" : pointsDelta < 0 ? "debit" : "neutral",
        detail: null,
      };
  }
}

/**
 * The two "after" figures, kept distinct. A reservation leaves the
 * balance alone and raises the held count; only finalization lowers the
 * balance. Showing one number for both is how a hold ends up looking
 * like a debit.
 */
export function describeLedgerAfter(entry) {
  const e = entry || {};
  const balanceAfter = intOrNull(e.balance_after);
  const reservedAfter = intOrNull(e.reserved_after);
  const parts = [];
  if (balanceAfter != null) parts.push(`balance ${balanceAfter}`);
  if (reservedAfter != null) parts.push(`held ${reservedAfter}`);
  return parts.join(" · ");
}
